import {
  faArrowsRotate,
  faCalendar,
  faFloppyDisk,
  faPenToSquare,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Skeleton } from '@mui/material';
import moment from 'moment';
import { useContext, useState } from 'react';

import { BacktestingContext } from '../../../../providers/backtesting/backtesting-provider';
import { tokens } from '../../../../styles/tokens';
import { SaveBacktestConfigModal } from './save-config-modal';

import './styles.scss';

const skeletonSx = { bgcolor: tokens.bg.tableLine };

const formatDateRange = (start: Date | null, end: Date | null) => {
  if (!start || !end) return 'Date range unavailable';
  return `${moment.utc(start).format('YYYY-MM-DD')} to ${moment.utc(end).format('YYYY-MM-DD')}`;
};

export const BacktestResultHeader = ({ isLoading }: { isLoading: boolean }) => {
  const {
    activeBacktestId,
    getActiveBacktest,
    runBacktest,
    showBacktestForm,
    setActiveBacktestResultTab,
    saveBacktestConfig,
    updateBacktestConfig,
    activeSavedConfigName,
    isSavingConfig,
  } = useContext(BacktestingContext);

  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const activeBacktest = getActiveBacktest();
  if (!activeBacktest || !activeBacktestId) return null;

  const { globalConfig, execution } = activeBacktest;

  const frequencyLabel =
    globalConfig.frequency != null && globalConfig.frequency > 0
      ? `${globalConfig.frequency}h frequency`
      : 'frequency';

  const handleEditConfig = () => {
    // Edit config == open the Specification tab (the editable form) and switch
    // the top header back to its form/config variant.
    showBacktestForm(activeBacktestId);
    setActiveBacktestResultTab(activeBacktestId, 'specification');
  };

  const handleSaveConfig = () => {
    setSaveModalOpen(true);
  };

  const handleUpdateConfig = () => {
    updateBacktestConfig();
  };

  return (
    <div className="backtest-result-header">
      {isLoading ? (
        <div className="backtest-header-result-loading">
          <Skeleton variant="text" width={140} height={60} animation="wave" sx={skeletonSx} />
          <Skeleton variant="text" width={360} height={60} animation="wave" sx={skeletonSx} />
          <Skeleton variant="text" width={260} height={60} animation="wave" sx={skeletonSx} />
        </div>
      ) : (
        <div className="backtest-result-header-context">
          <span className="backtest-result-header-title">Options Backtest</span>
          <span className="backtest-result-header-token">{globalConfig.token}</span>
          <span className="backtest-result-header-dot" />
          <span className="backtest-result-header-frequency">{frequencyLabel}</span>
          <span className="backtest-result-header-dot" />
          <span>
            {formatDateRange(globalConfig.backtestPeriod.start, globalConfig.backtestPeriod.end)}
          </span>
          <span className="backtest-result-header-separator">|</span>
          <span className="backtest-result-header-calculated-on">Calculated on:</span>
          <span className="backtest-result-header-executed-at">
            <FontAwesomeIcon icon={faCalendar} />
            {execution.executedAt
              ? moment.utc(execution.executedAt).format('DD MMM YYYY - HH:mm [UTC]')
              : 'Pending'}
          </span>
        </div>
      )}

      <div className="backtest-result-header-actions">
        <Button
          className="btn-update-config"
          onClick={handleUpdateConfig}
          disabled={!activeSavedConfigName || isSavingConfig || isLoading}
          startIcon={<FontAwesomeIcon icon={faArrowsRotate} />}
        >
          Update
        </Button>

        <Button
          className="btn-edit-config"
          onClick={handleEditConfig}
          disabled={isLoading}
          startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
        >
          Edit
        </Button>

        <Button
          className="btn-save-config"
          onClick={handleSaveConfig}
          disabled={isSavingConfig || isLoading}
          startIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
        >
          Save
        </Button>

        <Button
          className="btn-rerun-backtest"
          onClick={() => void runBacktest()}
          disabled={isLoading}
        >
          Rerun backtest
        </Button>
      </div>

      <SaveBacktestConfigModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={saveBacktestConfig}
      />
    </div>
  );
};
